import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";
import {
    selectProductDetails,
    selectProductDetailsLoading,
    deleteProduct,
    selectProductDeleteLoading,
    selectProductDeleteError
} from "../../redux/products/productsSlice";
import { closeModal, setHasToRefresh, setModal } from "../../redux/rightModalSlice";

export default function ViewProduct() {
    const dispatch = useDispatch();
    const productDetails = useSelector(selectProductDetails);
    const loading = useSelector(selectProductDetailsLoading);
    const deleteLoading = useSelector(selectProductDeleteLoading);
    const deleteError = useSelector(selectProductDeleteError);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');

    const formatPrice = (price) => {
        if (price === undefined || price === null) return "--";
        return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(price);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "--";
        return new Date(dateString).toLocaleDateString('it-IT');
    };

    const handleEditClick = () => {
        dispatch(setModal({ mode: 'edit', id: productDetails._id }));
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const confirmDelete = () => {
        if (productDetails?._id) {
            dispatch(deleteProduct({
                productId: productDetails._id,
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(closeModal());
                    dispatch(setHasToRefresh());
                }
            });
        }
    };

    if (loading) {
        return <LoadingSection />;
    }

    if (!productDetails) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold mb-4">Prodotto non trovato</h2>
                <p className="text-gray-600">Il prodotto richiesto non e' disponibile.</p>
                <div className="mt-4">
                    <button className="secondary" onClick={() => dispatch(closeModal())}>
                        <DynamicIcon iconClass="fas fa-arrow-left" />
                        Torna indietro
                    </button>
                </div>
            </div>
        );
    }

    // Categorie: usa il nuovo array categories se presente, altrimenti il vecchio campo singolo
    const categories = productDetails.categories && productDetails.categories.length > 0
        ? productDetails.categories
        : productDetails.categoryId
            ? [{ categoryId: productDetails.categoryId, categoryName: productDetails.categoryName }]
            : [];

    // Media
    const media = productDetails.media || [];
    const coverMedia = media.find(m => m.cover) || media[0] || null;

    return (
        <div className="p-6">
            {deleteLoading && <LoadingSection isFixed />}

            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-2xl font-bold">{productDetails.title || "Prodotto"}</h2>
                    {productDetails.code && (
                        <p className="text-sm text-gray-500 mt-1">Codice: {productDetails.code}</p>
                    )}
                </div>
                <div className="flex space-x-2 flex-shrink-0">
                    <button className="secondary" onClick={handleEditClick}>
                        <DynamicIcon iconClass="fas fa-edit" />
                        Modifica
                    </button>
                    <button className="danger" onClick={handleDeleteClick}>
                        <DynamicIcon iconClass="fas fa-trash" />
                        Elimina
                    </button>
                </div>
            </div>

            {/* Stato */}
            <div className="mb-6">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                    productDetails.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                    {productDetails.active ? 'Attivo' : 'Inattivo'}
                </span>
            </div>

            {/* SEZIONE: Dati Generali */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Dati Generali
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Codice</h4>
                        <p className="text-base font-semibold">{productDetails.code || "--"}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Nome</h4>
                        <p className="text-base font-semibold">{productDetails.title || "--"}</p>
                    </div>
                    <div className="md:col-span-2">
                        <h4 className="text-sm font-medium text-gray-500">Descrizione</h4>
                        <p className="text-base">{productDetails.description || "--"}</p>
                    </div>
                </div>
            </div>

            {/* SEZIONE: Categorie */}
            {categories.length > 0 && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Categorie
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {categories.map((cat) => (
                            <span
                                key={cat.categoryId}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                            >
                                <DynamicIcon iconClass="fa-solid fa-tag text-xs mr-1.5" />
                                {cat.categoryName}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* SEZIONE: Fornitore e Codici */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Fornitore e Codici
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Fornitore</h4>
                        <p className="text-base font-semibold">{productDetails.supplierLabel || "--"}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Codice Fornitore</h4>
                        <p className="text-base font-semibold">{productDetails.supplierCode || "--"}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Matricola</h4>
                        <p className="text-base font-semibold">{productDetails.serialNumber || "--"}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Lotto</h4>
                        <p className="text-base font-semibold">{productDetails.batchNumber || "--"}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Data Scadenza</h4>
                        <p className="text-base font-semibold">{formatDate(productDetails.expiryDate)}</p>
                    </div>
                </div>
            </div>

            {/* SEZIONE: Commerciale */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Dati Commerciali
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Prezzo di Vendita</h4>
                        <p className="text-base font-semibold">{formatPrice(productDetails.sellPrice)}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Prezzo di Listino</h4>
                        <p className="text-base font-semibold">{formatPrice(productDetails.listPrice)}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Aliquota IVA</h4>
                        <p className="text-base font-semibold">
                            {productDetails.vatLabel || (productDetails.vatValue !== undefined && productDetails.vatValue !== "" ? `${productDetails.vatValue}%` : "--")}
                        </p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Unita' di Acquisto</h4>
                        <p className="text-base font-semibold">{productDetails.unitaLabel || "--"}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Quantita'</h4>
                        <p className="text-base font-semibold">{productDetails.quantity ?? "--"}</p>
                    </div>
                </div>
            </div>

            {/* SEZIONE: Media */}
            {media.length > 0 && (
                <div className="bg-white shadow-md rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                        Media
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {media
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((item, index) => {
                                const isImage = item.kind && item.kind.startsWith('image/');
                                return (
                                    <div
                                        key={index}
                                        className={`relative border-2 rounded-lg overflow-hidden ${item.cover ? 'border-yellow-400' : 'border-gray-200'}`}
                                    >
                                        {item.cover && (
                                            <div className="absolute top-1 left-1 z-10 bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded">
                                                Copertina
                                            </div>
                                        )}
                                        {isImage ? (
                                            <img
                                                src={item.url}
                                                alt={item.fileName || `Media ${index + 1}`}
                                                className="w-full h-32 object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                                                <DynamicIcon iconClass="fa-solid fa-video text-2xl text-gray-400" />
                                            </div>
                                        )}
                                        <div className="px-2 py-1 text-xs text-gray-500 truncate">
                                            {item.fileName || `Media ${index + 1}`}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* Date */}
            <div className="bg-white shadow-md rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Data Creazione</h4>
                        <p className="text-base font-semibold">{formatDate(productDetails.creationDate)}</p>
                    </div>
                    <div>
                        <h4 className="text-sm font-medium text-gray-500">Ultimo Aggiornamento</h4>
                        <p className="text-base font-semibold">{formatDate(productDetails.lastUpdateDate)}</p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end">
                <button className="secondary" onClick={() => dispatch(closeModal())}>
                    <DynamicIcon iconClass="fas fa-times" />
                    Chiudi
                </button>
            </div>

            {/* Dialog conferma eliminazione */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Conferma Eliminazione</h3>
                        <p className="mb-6">
                            Sei sicuro di voler eliminare il prodotto "{productDetails.title}"?
                            Questa azione non puo' essere annullata.
                        </p>

                        {deleteError && (
                            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
                                <p>{deleteError}</p>
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                className="secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleteLoading}
                            >
                                Annulla
                            </button>
                            <button
                                className="danger"
                                onClick={confirmDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? (
                                    <>
                                        <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                        Eliminazione...
                                    </>
                                ) : (
                                    <>
                                        <DynamicIcon iconClass="fas fa-trash" />
                                        Elimina
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
